import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
} from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import { AddSvipData, uploadFile } from "../../DAL/create";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";

const AddSvip = ({ loading = false }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    svip_no: "",
    activationThreshold: "",
    bulletMessage: "",
    privateBulletMessage: "",
    canLive: "false",
    canKick: "false",
    canMute: "false",
    miniProfileColorful: "false",
    one_to_one: "false",
    colorCode: "",
    pricelist: {
      30: "",
    },
  });

  const [images, setImages] = useState({});
  const [uploaded, setUploaded] = useState({});
  const [imageFiles, setImageFiles] = useState({});
  const [uploading, setUploading] = useState({});
  const [fileError, setFileError] = useState("");

  const imageFields = [
    "frameImageId",
    "frameEffectId",
    "nicknameImageId",
    "nicknameEffectId",
    "entranceEffectImageId",
    "entranceEffectEffectId",
    "batchImageId",
    "batchEffectId",
    "backgroundImageId",
    "backgroundEffectId",
  ];

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePriceChange = (day, value) => {
    setForm((prev) => ({
      ...prev,
      pricelist: {
        ...prev.pricelist,
        [day]: Number(value),
      },
    }));
  };

  const handleImageChange = (field, file) => {
    setImageFiles((prev) => ({ ...prev, [field]: file }));
  };

  const handleImageUpload = async (field) => {
    const file = imageFiles[field];
    if (!file) {
      setFileError(`Please select a file for ${field}`);
      return;
    }

    setUploading((prev) => ({ ...prev, [field]: true }));
    const formData = new FormData();
    formData.append("image", file);

    try {
      const res = await uploadFile(formData);
      const imageId = res.data.id;
      setImages((prev) => ({ ...prev, [field]: imageId }));
      setUploaded((prev) => ({ ...prev, [field]: true }));
      enqueueSnackbar(`${field} uploaded`, { variant: "success" });
    } catch {
      enqueueSnackbar(`Failed to upload ${field}`, { variant: "error" });
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async () => {
    // ✅ Required fields check
    const requiredFields = [
      { key: "svip_no", label: "SVIP No" },
      { key: "activationThreshold", label: "Activation Threshold" },
      { key: "bulletMessage", label: "Bullet Message" },
      { key: "privateBulletMessage", label: "Private Bullet Message" },
      { key: "canLive", label: "Can Live" },
      { key: "canKick", label: "Can Kick" },
      { key: "canMute", label: "Can Mute" },
      { key: "miniProfileColorful", label: "Mini Profile Colorful" },
      { key: "one_to_one", label: "One to One" },
      { key: "pricelist", label: "Pricing List (30 days)" },
    ];

    for (let field of requiredFields) {
      if (
        form[field.key] === undefined ||
        form[field.key] === "" ||
        form[field.key] === null
      ) {
        enqueueSnackbar(`Please fill ${field.label}`, { variant: "error" });
        return; // stop execution if validation fails
      }
    }

    // 🔍 Validate 30-day price
    if (!form.pricelist[30] || Number(form.pricelist[30]) <= 0) {
      enqueueSnackbar("Please enter a valid price for 30 days", {
        variant: "error",
      });
      return;
    }

    const missingImages = imageFields.filter((field) => !images[field]);
    if (missingImages.length > 0) {
      enqueueSnackbar("Please upload all required images", {
        variant: "error",
      });
      return;
    }
    const payload = {
      ...form,
      svip_no: Number(form.svip_no),
      activationThreshold: Number(form.activationThreshold),
      bulletMessage: Number(form.bulletMessage),
      privateBulletMessage: Number(form.privateBulletMessage),
      colorCode: form.colorCode,
      canLive: form.canLive === "true",
      canKick: form.canKick === "true",
      canMute: form.canMute === "true",
      miniProfileColorful: form.miniProfileColorful === "true",
      one_to_one: form.one_to_one === "true",
      pricelist: [
        {
          name: "30 days",
          expireInDay: 30,
          pricing: form.pricelist[30],
          totalDailyDiamond: form.pricelist[30],
        },
      ],
      ...images,
    };

    const resp = await AddSvipData(payload);
    if (resp.status === 201 || resp.status === 200) {
      enqueueSnackbar("Svip Created Successfully", { variant: "success" });
      navigate("/sviplist");
    } else {
      enqueueSnackbar(
        resp?.message[0]?.constraints ||
          resp?.message ||
          resp?.data?.message ||
          resp?.error ||
          resp?.data?.error ||
          "Can't Do This Task Right Now",
        { variant: "error" }
      );
    }
  };

  const booleanOptions = [
    { label: "True", value: "true" },
    { label: "False", value: "false" },
  ];

  return (
    <Box maxWidth={500} mx="auto" mt={4} display="flex" flexDirection="column">
      <Typography variant="h5" mb={2}>
        Create SVIP
      </Typography>

      <TextField
        label="Name"
        value={form.name}
        onChange={(e) => handleChange("name", e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="SVIP Number"
        value={form.svip_no}
        onChange={(e) => handleChange("svip_no", e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Activation Threshold"
        type="number"
        value={form.activationThreshold}
        inputProps={{ min: 0 }}
        onChange={(e) => handleChange("activationThreshold", e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Bullet Message"
        type="number"
        value={form.bulletMessage}
        inputProps={{ min: 0 }}
        onChange={(e) => handleChange("bulletMessage", e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Private Bullet Message"
        type="number"
        value={form.privateBulletMessage}
        inputProps={{ min: 0 }}
        onChange={(e) => handleChange("privateBulletMessage", e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />

      {[
        "canLive",
        "canKick",
        "canMute",
        "miniProfileColorful",
        "one_to_one",
      ].map((field) => (
        <FormControl key={field} fullWidth sx={{ mb: 2 }}>
          <InputLabel>{field}</InputLabel>
          <Select
            value={form[field]}
            label={field}
            onChange={(e) => handleChange(field, e.target.value)}
          >
            {booleanOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ))}

      {imageFields.map((field) => (
        <Box key={field} sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {field.replace(/([A-Z])/g, " $1").trim()}
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleImageChange(field, e.target.files[0])}
          />
          <Button
            variant={uploaded[field] ? "contained" : "outlined"}
            color={uploaded[field] ? "success" : "primary"}
            size="small"
            onClick={() => handleImageUpload(field)}
            disabled={uploading[field]}
            sx={{ mt: 1 }}
          >
            {uploading[field] ? (
              <CircularProgress size={20} />
            ) : uploaded[field] ? (
              "Uploaded"
            ) : (
              "Upload"
            )}
          </Button>
          {/* {images[field] && (
            <Typography variant="caption">Image ID: {images[field]}</Typography>
          )} */}
        </Box>
      ))}

      <TextField
        label="Price for 30 days"
        type="number"
        value={form.pricelist[30]}
        onChange={(e) => handlePriceChange(30, e.target.value)}
        fullWidth
        inputProps={{ min: 0 }}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Color Code"
        value={form.colorCode}
        onChange={(e) => handleChange("colorCode", e.target.value)}
        fullWidth
        inputProps={{ min: 0 }}
        sx={{ mb: 2 }}
      />

      <Button variant="contained" onClick={handleSubmit} disabled={loading}>
        {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
      </Button>

      {fileError && (
        <FormHelperText sx={{ color: "red" }}>{fileError}</FormHelperText>
      )}
    </Box>
  );
};

export default AddSvip;
